import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

interface Product {
  id: string;
  name: string;
  product_code: string;
  brand: string;
  barcode: string;
  purchase_price: number;
  sale_price: number;
  stock: number;
}

interface Props {
  dealerId: string;
  supplierId: string;
  supplierName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function SupplierProductAddModal({ dealerId, supplierId, supplierName, onClose, onSuccess }: Props) {
  const [search, setSearch] = useState("");
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select("id, name, product_code, brand, barcode, purchase_price, sale_price, stock")
        .eq("dealer_id", dealerId)
        .order("name");
      setAllProducts(data || []);
      setLoading(false);
    };
    load();
  }, [dealerId]);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setResults([]);
      return;
    }
    const filtered = allProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.product_code || "").toLowerCase().includes(q) ||
        (p.barcode || "").includes(q) ||
        (p.brand || "").toLowerCase().includes(q)
    );
    setResults(filtered.slice(0, 10));
  }, [search, allProducts]);

  useEffect(() => {
    setTotalAmount(unitPrice * quantity);
  }, [unitPrice, quantity]);

  const selectProduct = (product: Product) => {
    setSelected(product);
    setUnitPrice(Number(product.purchase_price));
    setQuantity(1);
    setSearch("");
    setResults([]);
  };

  const handleAdd = async () => {
    if (!selected || quantity <= 0) return;
    setSaving(true);
    try {
      // Insert purchase record
      const { error: purchaseErr } = await supabase
        .from("purchases")
        .insert({
          dealer_id: dealerId,
          supplier_id: supplierId,
          product_id: selected.id,
          quantity,
          unit_price: unitPrice,
          total_amount: totalAmount,
          type: "purchase",
          note: `${supplierName} firmasından alım`,
        });

      if (purchaseErr) throw purchaseErr;

      // Increase stock
      const newStock = (selected.stock || 0) + quantity;
      const { error: stockErr } = await supabase
        .from("products")
        .update({ stock: newStock })
        .eq("id", selected.id);

      if (stockErr) throw stockErr;

      setSuccess(true);
      onSuccess();
    } catch (_err) {
      // fail silently
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {success ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-2xl text-emerald-600"></i>
            </div>
            <h4 className="text-base font-semibold text-foreground-950 mb-1">Ürün Alındı!</h4>
            <p className="text-sm text-foreground-500 mb-1">
              <strong className="text-foreground-900">{selected?.name}</strong> &middot; {quantity} adet
            </p>
            <p className="text-sm text-foreground-400 mb-1">
              Birim: <strong className="text-foreground-900">${unitPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>
            </p>
            <p className="text-sm text-foreground-400 mb-6">
              Stok <strong className="text-emerald-600">+{quantity}</strong> arttırıldı
            </p>
            <button onClick={onClose} className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
              Kapat
            </button>
          </div>
        ) : selected ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">Tedarikçiden Ürün Al</h4>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-arrow-left-line"></i>
              </button>
            </div>

            <div className="bg-background-100 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-foreground-500">Tedarikçi</span>
                <span className="text-sm font-semibold text-foreground-950">{supplierName}</span>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                <div className="w-12 h-12 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                  <i className="ri-box-3-line text-foreground-400 text-lg"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 truncate">{selected.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {selected.brand && <span className="text-[10px] text-foreground-400 bg-background-50 px-1.5 py-0.5 rounded">{selected.brand}</span>}
                    {selected.product_code && <span className="text-[10px] text-foreground-400 font-mono">{selected.product_code}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-foreground-500">Alış: <strong className="text-emerald-600">${Number(selected.purchase_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></span>
                    <span className="text-xs text-foreground-500">Mevcut Stok: <strong className="text-foreground-900">{selected.stock || 0}</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Adet</label>
                <div className="flex items-center gap-2">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer">
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                    className="w-20 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <button onClick={() => setQuantity(quantity + 1)} className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer">
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Birim Alış Fiyatı ($)</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="bg-emerald-50 rounded-xl p-3 mb-4 border border-emerald-200/70">
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-700 font-medium">Toplam Alış Tutarı</span>
                <span className="text-base font-bold text-emerald-700">${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              <p className="text-xs text-emerald-600/70 mt-1">Stok <strong>+{quantity}</strong> artacak</p>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setSelected(null)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                Geri
              </button>
              <button
                onClick={handleAdd}
                disabled={saving || quantity <= 0}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {saving ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Alınıyor...</span> : <>Alımı Kaydet (${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })})</>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">{supplierName} - Ürün Al</h4>
              <button onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="relative mb-3">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ürün adı, kod veya barkod ile arayın..."
                autoFocus
                className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
              </div>
            ) : search.trim() === "" ? (
              <div className="text-center py-8 text-foreground-400">
                <i className="ri-search-line text-3xl block mb-2"></i>
                <p className="text-sm">Ürün aramak için yazmaya başlayın</p>
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-8 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl block mb-2"></i>
                <p className="text-sm">Ürün bulunamadı</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {results.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => selectProduct(product)}
                    className="flex items-center gap-3 p-3 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer border border-transparent hover:border-emerald-200"
                  >
                    <div className="w-10 h-10 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                      <i className="ri-box-3-line text-foreground-400"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">{product.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {product.brand && <span className="text-[10px] text-foreground-400">{product.brand}</span>}
                        {product.product_code && <span className="text-[10px] text-foreground-400 font-mono">{product.product_code}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-emerald-600">${Number(product.purchase_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                      <p className="text-[10px] text-foreground-400">Stok: {product.stock || 0}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}